<script setup>
  import { getEmployerUrl } from '@/utils/url'
  import { notify } from 'notiwind'
  import { askLogin } from '@/utils/ask-login'

  const props = defineProps({
    isCareerSite: { default: false, type: Boolean },
    job: { required: true, type: Object },
    color: { default: '#58d9db', type: String },
    hasApplied: { default: false, type: Boolean }
  })

  const emit = defineEmits(['apply'])
  const { path } = useRoute()
  const { user, fetchUser } = useUser()
  const { create, delete: del } = useStrapi()

  const favorite = ref(user.value?.favorites?.find(favorite => favorite.job.id === props.job.id))

  const toggleFavorite = {
    onClick: async job => {
      if (toggleFavorite.toggling.value) return
      toggleFavorite.toggling.value = true
      try {
        if (favorite.value) {
          await del('favorites', favorite.value.id)
          favorite.value = null
        } else {
          const newFavorite = await create('favorites', { user: user.value.id, job: job.id })
          favorite.value = newFavorite.data
        }
      } catch (error) {
        notify({
          group: 'main',
          type: 'danger',
          title: "Une erreur s'est produite",
          text: 'Si le problème persiste, merci de contacter notre équipe'
        })
      } finally {
        await fetchUser()
      }
      toggleFavorite.toggling.value = false
    },
    toggling: ref(false)
  }
</script>

<template>
  <section :class="['relative flex-1 space-y-6 px-4 py-4 sm:px-8 lg:px-12 lg:pb-24 xl:px-16 xl:py-8', isCareerSite && 'lg:ml-80']">
    <FavoriteToggle v-if="!isCareerSite" customClass="absolute right-4 top-4" :job="job" :route="path" size="xl" />
    <div class="flex flex-col items-center justify-center space-y-2 sm:flex-row sm:space-x-4 sm:space-y-0" v-if="job.speaker">
      <Profile
        :profile="{
          linkedin: job.speaker.linkedin,
          photo: job.speaker.photo?.url ? useStrapiMedia(job.speaker.photo.url) : undefined
        }"
      />
      <div class="text-center text-lg font-bold text-zinc-500 sm:text-left">
        Cette offre est présentée par<br />{{
          `${job.speaker.firstName} ${job.speaker.lastName}${job.speaker.function ? `, ${job.speaker.function}` : ''}`
        }}
      </div>
    </div>

    <div class="flex flex-col space-y-3" v-if="job.answer2">
      <template v-if="job.enhanceWithAI">
        <div class="font-special text-lg font-bold md:text-xl">Le défi à relever&nbsp;?</div>
        <div v-html="job.answer2.replace(/\n/g, '<br />')" :class="['leading-[1.4]', !isCareerSite && 'font-main']" />
      </template>
      <template v-else>
        <h3 class="font-special text-lg font-bold sm:text-xl">Présentation</h3>
        <Markdown :class="isCareerSite ? 'text-[#1f1f1f] marker:prose-ul:!text-[#1f1f1f]' : 'font-main'" :content="job.answer2" />
        <NuxtLink class="job-link underline decoration-2 underline-offset-2" v-if="isCareerSite" :to="path.split('/jobs')[0]"
          >Découvrez notre institution
        </NuxtLink>
        <NuxtLink class="font-main underline duration-300 hover:brightness-75" v-else :to="getEmployerUrl(job.employer)" :style="{ color: color }">
          Découvrez notre institution
        </NuxtLink>
      </template>
    </div>

    <div class="space-y-3" v-if="job.answer1">
      <h3 class="font-special text-lg font-bold md:text-xl">
        <template v-if="job.enhanceWithAI">Les missions qui attendent les candidats en quelques mots&nbsp;?</template>
        <template v-else>Description des missions</template>
      </h3>
      <Markdown :class="isCareerSite ? 'text-[#1f1f1f] marker:prose-ul:!text-[#1f1f1f]' : 'font-main'" :content="job.answer1" />
    </div>

    <div class="space-y-3" v-if="job.advantages">
      <h3 class="text-lg font-bold sm:text-xl">Avantages</h3>
      <Markdown :class="isCareerSite ? 'text-[#1f1f1f] marker:prose-ul:!text-[#1f1f1f]' : 'font-main'" :content="job.advantages" />
    </div>

    <div class="space-y-3" v-if="job.employer?.visible">
      <h3 class="font-special text-lg font-bold md:text-xl">L'environnement de travail</h3>
      <div :class="!isCareerSite && 'font-main'">
        Parcourez les
        <NuxtLink class="job-link underline decoration-2 underline-offset-2" v-if="isCareerSite" :to="path.split('/jobs')[0]"
          >coulisses de notre organisation
        </NuxtLink>
        <NuxtLink class="font-main underline duration-300 hover:brightness-75" v-else :to="getEmployerUrl(job.employer)" :style="{ color: color }">
          coulisses de notre organisation
        </NuxtLink>
        pour en savoir plus sur notre équipe et nos projets innovants.
      </div>
    </div>

    <div class="mt-8 flex flex-col justify-between space-x-0 space-y-3 md:flex-row md:space-x-8 md:space-y-0" v-if="job.answer3">
      <div class="flex-1 space-y-4">
        <h3 class="font-special text-lg font-bold md:text-xl">
          <template v-if="job.enhanceWithAI">️Ce poste est fait pour vous si&nbsp;:</template>
          <template v-else>Profil recherché</template>
        </h3>
        <Markdown v-if="job.answer3" :class="isCareerSite ? 'text-[#1f1f1f] marker:prose-ul:!text-[#1f1f1f]' : 'font-main'" :content="job.answer3" />
      </div>

      <div class="flex-1 self-center" v-if="job.enhanceWithAI">
        <div
          class="relative mt-2 rounded-md bg-zinc-100 p-4 shadow sm:mr-4 sm:px-6 md:px-4 lg:px-6"
          v-if="job.crushes || job.employer?.defaultCrushes"
        >
          <div class="font-special text-lg font-bold">Les petits +</div>
          <ul class="mt-4 space-y-2">
            <li
              v-for="(item, index) in (job.crushes || job.employer?.defaultCrushes).split('\n')"
              v-text="item"
              :class="!isCareerSite && 'font-main'"
              :key="index"
            />
          </ul>
        </div>
      </div>
    </div>

    <div class="space-y-3" v-if="job.jobSheet && (job.jobSheetVisible || job.jobSheetVisible === undefined)">
      <NuxtLink class="font-bold sm:text-lg" target="_blank" :to="useStrapiMedia(job.jobSheet.url)"
        >👉 Téléchargez l'<span class="job-link underline decoration-2 underline-offset-2">offre d'emploi</span></NuxtLink
      >
    </div>

    <div class="mt-8">
      <JobApplyButton :job="job" :color="color" :hasApplied="hasApplied" :isCareerSite="isCareerSite" @apply="$emit('apply')" />
    </div>

    <div class="mt-4 text-center text-zinc-500" v-if="job.expiresAt">
      Date limite de candidature&nbsp;:
      {{ new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(job.expiresAt)) }}
    </div>
  </section>
</template>
